import { scrubPiiForLlm } from "../pii-scrubber.js";
import { READ_PEOPLE_MINE } from "../registry/pages/read-people-mine.js";
import { findEnumChoiceInSentence, resolveEnumSlot } from "../resolvers/enum-slot.js";
import { sameWord, wordsOf, type Word } from "../resolvers/vi-words.js";
import type { EnumFallbackPageLike, EnumSlotFill } from "./enum-fallback.js";
import { judgeSpan, type InterpreterSpanLike } from "./output.js";

/**
 * 🔴 READ-PEOPLE-MINE-WRONG-ANSWER (@UC0207) — LƯỚI TẤT ĐỊNH chỉ số `thành viên` của READ-PEOPLE-MINE cho câu hỏi VỀ NGƯỜI theo khuôn ĐÓNG.
 *
 * ## Vấn đề (đo, không đoán)
 * `ai_audit_log` thế giới L2, LLM thật 02/10: *"ai là thủ quỹ"* ⇒ Phiếu chọn ĐÚNG trang READ-PEOPLE-MINE nhưng `spans: []` 2/3 (hanh) ⇒ ô `chiSo`
 * rơi mặc định của Sổ (`fallback: "khoản của tôi"`, US-AIV2-0111 C10) ⇒ *"Ở nhóm Lớp 2A Kim Đồng, bạn không còn khoản nào phải đóng."* — trả lời
 * một câu người hỏi KHÔNG hỏi. *"ai đang giữ quỹ lớp"* (tu) `spans: []` 1/1. L04 (01/10): *"nhóm có những thành viên nào"* 2/3 không gạch ô.
 * Sổ ĐÃ có đồng nghĩa ("thủ quỹ", "ai là", "những ai"…) — khi model gạch, resolver đọc đúng; cái hỏng là model KHÔNG gạch. Sửa prompt là đổi thứ
 * model thấy cho mọi ca (thu lại eval) mà vẫn là xác suất ⇒ chốt CODE, 0 LLM.
 *
 * ## Luật (MỘT hàm; Người làm gọi ở lượt mở việc từ câu — cùng chỗ với H02/S19/L07)
 * Điền ô `chiSo` của READ-PEOPLE-MINE khi và chỉ khi:
 *  0. trang là READ-PEOPLE-MINE (`READ_PEOPLE_MINE.id` của Sổ — không literal) — lưới mang NGHĨA của đúng một ô, không áp chéo trang;
 *  1. Phiếu ĐÃ NEO không có span nào cho `chiSo` — lưới KHÔNG BAO GIỜ đè chữ model đã gạch;
 *  2. MỌI chữ của câu (đã cạo PII) thuộc một khuôn ĐÓNG dưới đây (`isPeopleQuestion`) — câu hỏi DANH SÁCH NGƯỜI hoặc VAI, không gì khác;
 *  3. CẢ câu nhắc ĐÚNG MỘT lựa chọn của ô (`findEnumChoiceInSentence` — CÙNG phép đọc chữ với H02: nhãn/đồng nghĩa của Sổ, theo từ liền mạch), và
 *     lựa chọn đó là `thành viên`;
 *  4. đoạn chữ khớp qua phép neo-vào-câu (`judgeSpan`, phạm vi `null`) và resolver THẬT của ô đọc ra `thành viên`.
 * Chữ điền là CHỮ CỦA CÂU ("thủ quỹ", "ai là", "thành viên"…) — luật chung 1.
 *
 * Khuôn (so theo TỪ, `sameWord`: gõ không dấu khớp chữ có dấu; có dấu thì phải đúng dấu — "thu quỹ" ≠ "thủ quỹ"):
 *   HỎI-ĐẦU? = [cho [tôi|mình|em|chị|anh|con|cháu|bác]] hỏi | xin hỏi
 *   PHẠM-VI  = {nhóm|lớp|đội|quỹ|hội} [này|mình|ta|của {mình|tôi|chúng ta|chúng tôi}]
 *   VAI      = thủ quỹ | người giữ quỹ | quản trị [viên] | trưởng nhóm | chủ nhóm
 *   ĐUÔI     = {vậy|thế|nhỉ|ạ|à|hả|đấy|đó|nhé|nha|ơi}*
 *   (R1) HỎI-ĐẦU? [PHẠM-VI] ai [đang|hiện] {là|làm} VAI [[của] PHẠM-VI] ĐUÔI          — "ai là thủ quỹ", "ai làm thủ quỹ lớp mình"
 *   (R2) HỎI-ĐẦU? VAI [[của] PHẠM-VI] [hiện] là ai ĐUÔI                                — "thủ quỹ lớp mình là ai thế"
 *   (R3) HỎI-ĐẦU? ai [đang|hiện] giữ quỹ [{lớp|đội|nhóm|hội}] [này|mình|ta] ĐUÔI       — "ai đang giữ quỹ lớp"
 *   (M1) HỎI-ĐẦU? [PHẠM-VI] {có|gồm|gồm có|bao gồm} [những|các] {ai | thành viên [nào] | bao nhiêu {người|thành viên} | mấy {người|thành viên}} ĐUÔI
 *   (M2) HỎI-ĐẦU? [cho [tôi|mình|em|chị|anh] xem | xem | liệt kê] [danh sách] [các|những] thành viên [[của] PHẠM-VI] ĐUÔI  (≥1 chữ dẫn hoặc "danh sách")
 *   (M3) HỎI-ĐẦU? [những] ai [đang] {ở|trong|ở trong} PHẠM-VI ĐUÔI
 *   (M4) HỎI-ĐẦU? thành viên [[của] PHẠM-VI] {là|gồm|gồm có} [những] ai ĐUÔI
 *
 * ## Vì sao an toàn
 *  - Khuôn không có chỗ cho NGÔI 1 làm chủ ngữ ("mình còn nợ…"), cho trạng thái đóng ("ai chưa đóng", "những ai chưa đóng"), cho phủ định /
 *    nửa chắc ("không", "hình như"), cho tên riêng ("Lan là thủ quỹ à") hay cho câu ĐỔI vai ("cho chị Linh làm thủ quỹ") ⇒ ngoài khuôn ⇒ đường
 *    cũ (span model / mặc định Sổ). Câu hỏi nên KHÔNG đo `hasNegationOrDoubt` (luật chung 4 — lệch có địa chỉ, như M07/M09): "?" và "ai" là
 *    hình của chính câu hỏi; khuôn tự giới hạn phủ định (không có chỗ cho nó).
 *  - Trang ĐỌC, 0 phiếu, 0 ghi. Chỉ số `thành viên` đọc ĐÚNG cửa màn Thành viên (`listMembers` + mục `managers` ghim — vai hiện cho MỌI vai, màn
 *    Cài đặt nhóm "Danh sách quản trị viên" không gác quyền); không chạm tiền hay trạng thái đóng ⇒ khoá `payment_status_visibility` không liên quan.
 *  - Chiều sai tệ nhất: một câu hỏi về người nhận danh sách thành viên + vai thay vì câu khoản — trong phạm vi người hỏi VỐN được xem.
 *  - Lưới chỉ chạy trong trang ĐÃ mở (LLM thật chọn, hoặc lưới chọn trang đã qua luật chung 3) ⇒ không vòng qua kill-switch / LLM giả.
 *
 * ## Giới hạn đã biết
 *  - Câu gõ KHÔNG dấu chứa "quy" ("ai la thu quy") ⇒ `findEnumChoiceInSentence` coi "quy" là chữ hai nghĩa (quỹ/quý) ⇒ `null` ⇒ không điền (luật
 *    review #1070 M2 của H02, không nới ở đây). Câu không dấu không có "quy" ("nhom co nhung ai") vẫn điền.
 *  - Câu hỏi vai không có trong khuôn ("ai quản lý nhóm", "người giữ tiền quỹ là ai") ⇒ đường cũ.
 */
export const PEOPLE_QUESTION_CHOICE = "thành viên";
const PEOPLE_QUESTION_SLOT = "chiSo";

/**
 * Từ vựng ĐÓNG của khuôn — chữ CÓ DẤU chuẩn; mỗi từ của câu được ánh xạ về chữ chuẩn bằng `sameWord`. Hai mục chung dạng bỏ dấu mà CÙNG vai
 * trong khuôn (đuôi "à"/"ạ") gộp về một chữ chuẩn; hai mục khác vai ("hỏi"/"hội") ⇒ chữ không dấu "hoi" khớp hai chữ chuẩn ⇒ không đủ chắc.
 * Cố ý KHÔNG có "cô"/"chú" (trùng dạng bỏ dấu với "có"/"chủ" — câu không dấu "nhom co nhung ai" phải đọc được).
 */
const LEXICON: readonly { readonly word: Word; readonly canon: string }[] = [
  "cho", "tôi", "mình", "em", "chị", "anh", "con", "cháu", "bác", "hỏi", "xin",
  "nhóm", "lớp", "đội", "quỹ", "hội", "này", "ta", "của", "chúng",
  "thủ", "người", "giữ", "quản", "trị", "viên", "trưởng", "chủ",
  "ai", "đang", "hiện", "là", "làm",
  "có", "gồm", "bao", "những", "các", "thành", "nào", "nhiêu", "mấy",
  "xem", "liệt", "kê", "danh", "sách", "ở", "trong",
  "vậy", "thế", "nhỉ", "ạ", "à", "hả", "đấy", "đó", "nhé", "nha", "ơi",
].map((w) => ({ word: wordsOf(w)[0]!, canon: w === "à" ? "ạ" : w }));

const ASK = "(?:(?:cho (?:(?:tôi|mình|em|chị|anh|con|cháu|bác) )?hỏi|xin hỏi) )?";
const SCOPE = "(?:nhóm|lớp|đội|quỹ|hội)(?: (?:này|mình|ta|của (?:mình|tôi|chúng ta|chúng tôi)))?";
const ROLE = "(?:thủ quỹ|người giữ quỹ|quản trị(?: viên)?|trưởng nhóm|chủ nhóm)";
const TAIL = "(?: (?:vậy|thế|nhỉ|ạ|hả|đấy|đó|nhé|nha|ơi))*";
const OF_SCOPE = `(?: (?:của )?${SCOPE})?`;

/** Bảy khuôn (docblock). Neo hai đầu: MỌI chữ của câu phải thuộc khuôn. */
const PATTERNS: readonly RegExp[] = [
  `${ASK}(?:${SCOPE} )?ai (?:(?:đang|hiện) )?(?:là|làm) ${ROLE}${OF_SCOPE}${TAIL}`,
  `${ASK}${ROLE}${OF_SCOPE} (?:hiện )?là ai${TAIL}`,
  `${ASK}ai (?:(?:đang|hiện) )?giữ quỹ(?: (?:lớp|đội|nhóm|hội))?(?: (?:này|mình|ta))?${TAIL}`,
  `${ASK}(?:${SCOPE} )?(?:có|gồm|gồm có|bao gồm) (?:(?:những|các) )?(?:ai|thành viên(?: nào)?|(?:bao nhiêu|mấy) (?:người|thành viên))${TAIL}`,
  `${ASK}(?:(?:cho (?:(?:tôi|mình|em|chị|anh) )?xem|xem|liệt kê) (?:danh sách )?|danh sách )(?:(?:các|những) )?thành viên${OF_SCOPE}${TAIL}`,
  `${ASK}(?:những )?ai (?:đang )?(?:ở trong|ở|trong) ${SCOPE}${TAIL}`,
  `${ASK}thành viên${OF_SCOPE} (?:là|gồm|gồm có) (?:những )?ai${TAIL}`,
].map((p) => new RegExp(`^${p}$`, "u"));

/** Câu (đã cạo PII) thuộc một trong bảy khuôn câu hỏi VỀ NGƯỜI. Một từ ngoài từ vựng ⇒ `false` (không khuôn nào có chỗ cho nó). */
function isPeopleQuestion(text: string): boolean {
  const canon: string[] = [];
  for (const w of wordsOf(text)) {
    const hit = new Set(LEXICON.filter((l) => sameWord(w, l.word)).map((l) => l.canon));
    // 0 chữ chuẩn: chữ lạ (tên riêng, "chưa", "đóng"…). ≥2: chữ không dấu hai nghĩa trong chính từ vựng ("hoi") ⇒ không đủ chắc.
    if (hit.size !== 1) return false;
    canon.push([...hit][0]!);
  }
  const line = canon.join(" ");
  return line !== "" && PATTERNS.some((re) => re.test(line));
}

/**
 * @param sentence câu người dùng (thô hoặc đã cạo — hàm tự cạo, idempotent).
 * @param anchoredSpans span của Phiếu **ĐÃ NEO** cho CHÍNH trang này (`envSpans` của Người làm).
 * @param page trang đang mở (Sổ); `null` ⇒ không áp.
 */
export function fillPeopleIndicatorFromSentence(
  sentence: string,
  anchoredSpans: readonly InterpreterSpanLike[],
  page: (EnumFallbackPageLike & { readonly id: string }) | null,
): EnumSlotFill[] {
  if (page === null || page.id !== READ_PEOPLE_MINE.id) return [];
  const slot = page.slots.find((s) => s.id === PEOPLE_QUESTION_SLOT);
  if (slot === undefined || slot.type !== "enum") return [];
  if (anchoredSpans.some((s) => s.slot === slot.id)) return [];
  const text = scrubPiiForLlm(sentence).text.normalize("NFC").trim();
  if (!isPeopleQuestion(text)) return [];
  const match = findEnumChoiceInSentence(text, slot);
  if (match === null || match.choice !== PEOPLE_QUESTION_CHOICE) return [];
  const verdict = judgeSpan(text, { slot: slot.id, text: text.slice(match.start, match.end), start: match.start, end: match.end }, null);
  if (!verdict.kept) return [];
  const r = resolveEnumSlot(verdict.span.text, slot);
  if (r.status !== "resolved" || r.value !== PEOPLE_QUESTION_CHOICE) return [];
  return [{ slot: slot.id, text: verdict.span.text }];
}
